import { useRef, Activity, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import ConfirmationModal from '@/components/ConfirmationModal';
import { SegmentedControl } from '@/components/SegmentedControl';
import { ECommonStatus, ESubscriberType, ERestoreEntityType } from '@/enum';
import { useFormatText } from '@/hooks/useFormatText';
import {
  softdeleteRecord,
  blockUnblockRecordAPI,
} from '@/service/commonService';
import { ISelectedRowValue, ISubscriptionChildDetails } from '@/types';

import ChildParentDetailsModal from './modal/ChildParentDetailsModal';
import PersonalSubscriber from './PersonalSubscriber';
import SchoolSubscriber from './SchoolSubscriber';

const ChildrenPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isRemoveChildrenModalOpen, setIsRemoveChildrenModalOpen] =
    useState(false);
  const [isOpenBlockModal, setIsOpenBlockModal] = useState(false);
  const [selectedChildren, setSelectedChildren] =
    useState<ISelectedRowValue | null>(null);
  const [confirmationLoading, setConfirmationLoading] = useState(false);
  const { formatText } = useFormatText();
  const [subscriptionType, setSubscriptionType] = useState<ESubscriberType>(
    id === ESubscriberType.SCHOOL
      ? ESubscriberType.SCHOOL
      : ESubscriberType.PERSONAL
  );

  useEffect(() => {
    if (id === ESubscriberType.SCHOOL) {
      setSubscriptionType(ESubscriberType.SCHOOL);
    } else if (id === ESubscriberType.PERSONAL) {
      setSubscriptionType(ESubscriberType.PERSONAL);
    }
  }, [id]);

  const handleTabChange = (value: string) => {
    const type = value as ESubscriberType;
    setSubscriptionType(type);
    navigate(`/user-management/children/${type}`);
  };
  const [isChildDetailsModalOpen, setIsChildDetailsModalOpen] = useState(false);
  const childRef = useRef<{
    fetchChildrens: () => void;
    fetchSummary: () => void;
  }>(null);
  const childSchoolRef = useRef<{
    fetchSchoolChildrens: () => void;
    fetchSummary: () => void;
  }>(null);

  const handleRemoveChildren = async () => {
    if (!selectedChildren?.id) return;
    setConfirmationLoading(true);

    try {
      const params = {
        id: selectedChildren.id.toString(),
        record_type:
          subscriptionType === ESubscriberType.PERSONAL
            ? ERestoreEntityType.CHILD
            : ERestoreEntityType.STUDENT,
      };
      const response = await softdeleteRecord(params);
      if (response?.status) {
        if (subscriptionType === ESubscriberType.PERSONAL) {
          childRef.current?.fetchChildrens();
          childRef.current?.fetchSummary();
        } else {
          childSchoolRef.current?.fetchSchoolChildrens();
          childSchoolRef.current?.fetchSummary();
        }
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setConfirmationLoading(false);
      setIsRemoveChildrenModalOpen(false);
      setSelectedChildren(null);
    }
  };

  const handleBlockChildren = async (
    childrenProvided?: ISelectedRowValue | ISubscriptionChildDetails
  ) => {
    const children = childrenProvided || selectedChildren;
    if (!children || !children.id) return;

    setConfirmationLoading(true);

    try {
      const params = {
        id: children.id,
        record_type:
          subscriptionType === ESubscriberType.SCHOOL
            ? ERestoreEntityType.STUDENT
            : ERestoreEntityType.CHILD,
        is_block: children.status === ECommonStatus.ACTIVE,
      };
      const response = await blockUnblockRecordAPI(params);

      if (response?.status) {
        if (subscriptionType === ESubscriberType.PERSONAL) {
          childRef.current?.fetchChildrens();
          childRef.current?.fetchSummary();
        } else {
          childSchoolRef.current?.fetchSchoolChildrens();
          childSchoolRef.current?.fetchSummary();
        }
      } else {
        console.error(response?.message);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setConfirmationLoading(false);
      setIsOpenBlockModal(false);
      if (!childrenProvided) {
        setSelectedChildren(null);
      }
    }
  };

  return (
    <>
      <SegmentedControl
        value={subscriptionType}
        onChange={handleTabChange}
        variant="underline"
        options={[
          {
            value: ESubscriberType.PERSONAL,
            label: formatText('children.tab.personal-subscriber'),
            disabled: false,
          },
          {
            value: ESubscriberType.SCHOOL,
            label: formatText('children.tab.school-subscriber'),
            disabled: false,
          },
        ]}
        className="mb-6"
      />

      {subscriptionType === ESubscriberType.PERSONAL ? (
        <PersonalSubscriber
          ref={childRef}
          setSelectedChildren={setSelectedChildren}
          setIsOpenBlockModal={setIsOpenBlockModal}
          setIsRemoveChildrenModalOpen={setIsRemoveChildrenModalOpen}
          handleBlockChildren={handleBlockChildren}
          isChildDetailsModalOpen={isChildDetailsModalOpen}
          setIsChildDetailsModalOpen={setIsChildDetailsModalOpen}
        />
      ) : (
        <SchoolSubscriber
          ref={childSchoolRef}
          setSelectedChildren={setSelectedChildren}
          setIsOpenBlockModal={setIsOpenBlockModal}
          setIsRemoveChildrenModalOpen={setIsRemoveChildrenModalOpen}
          handleBlockChildren={handleBlockChildren}
          setIsChildDetailsModalOpen={setIsChildDetailsModalOpen}
        />
      )}
      <Activity mode={isRemoveChildrenModalOpen ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isRemoveChildrenModalOpen}
          setIsOpen={setIsRemoveChildrenModalOpen}
          title="children.actions.remove-child-title"
          descriptionPrefix="children.actions.remove-child-prefix"
          descriptionSuffix="children.actions.remove-child-suffix"
          entityName={selectedChildren?.name}
          confirmLabel={formatText('children.actions.remove-child')}
          onConfirm={handleRemoveChildren}
          isLoading={confirmationLoading}
          isDestructive
        />
      </Activity>
      <Activity mode={isOpenBlockModal ? 'visible' : 'hidden'}>
        <ConfirmationModal
          isOpen={isOpenBlockModal}
          setIsOpen={setIsOpenBlockModal}
          title="children.actions.block-child-title"
          descriptionPrefix="children.actions.block-child-prefix"
          descriptionSuffix="children.actions.block-child-suffix"
          entityName={selectedChildren?.name}
          confirmLabel={formatText('children.actions.block-child')}
          onConfirm={handleBlockChildren}
          isLoading={confirmationLoading}
          isDestructive
        />
      </Activity>
      <Activity mode={isChildDetailsModalOpen ? 'visible' : 'hidden'}>
        <ChildParentDetailsModal
          setIsOpen={setIsChildDetailsModalOpen}
          selectedChildId={selectedChildren?.id}
          subscriberType={subscriptionType}
          isOpenBlockModal={isOpenBlockModal}
          setIsOpenBlockModal={setIsOpenBlockModal}
          setSelectedChildren={setSelectedChildren}
          handleBlockChildren={handleBlockChildren}
        />
      </Activity>
    </>
  );
};

export default ChildrenPage;
